import { useTranslation } from 'react-i18next';
import { ReturnStatusType } from 'returnCenter-api';
import ConfirmationDialog from 'components/ui/ConfirmationDialog';
import useAppSelector from 'hooks/useAppSelector';
import { returnSelectors } from 'store/reducers/returnSlice';
import { intercompanySelectors } from 'store/reducers/intercompanySlice';

export type OrderDialogsProps = {
    // Complete Return Dialog
    showCompleteDialog: boolean;
    completeReturnCheckbox: boolean;
    setCompleteReturnCheckbox: (v: boolean) => void;
    handleConfirmCompleteReturn: () => void;
    handleCancelCompleteReturn: () => void;

    // Complete Batch Return Dialog
    showCompleteBatchDialog: boolean;
    getUnassignedBatchItemCount: () => number;
    handleConfirmCompleteBatch: () => void;
    handleCancelCompleteBatch: () => void;

    // Delete Return Dialog
    showDeleteDialog: boolean;
    deleteReturnCheckbox: boolean;
    setDeleteReturnCheckbox: (v: boolean) => void;
    handleConfirmDeleteReturn: () => void;
    handleCancelDeleteReturn: () => void;

    // Processing Status Warning Dialog
    pendingProcessingStatus: ReturnStatusType | null;
    setPendingProcessingStatus: (v: ReturnStatusType | null) => void;
    handleSaveWithStatus: (status: ReturnStatusType) => void;
    clearHighlight: () => void;

    // Bulk Reason Change Dialog
    isDialogOpen: boolean;
    setIsDialogOpen: (v: boolean) => void;
    bulkActionCheckbox: boolean;
    setBulkActionCheckbox: (v: boolean) => void;
    handleConfirmBulkReason: () => void;
    cancelBulkReasonChange: () => void;

    // Bulk Max Qty Dialog
    showBulkMaxQtyDialog: boolean;
    bulkMaxQtyCheckbox: boolean;
    setBulkMaxQtyCheckbox: (v: boolean) => void;
    handleConfirmBulkMaxQty: () => void;
    handleCancelBulkMaxQty: () => void;

    // Remove Order Dialog
    pendingRemoveOrderNumber: string | null;
    handleConfirmRemoveOrder: () => void;
    handleCancelRemoveOrder: () => void;

    // Navigation Guard Dialog
    isNavBlocked: boolean;
    hasUnsavedChanges: boolean;
    handleConfirmNavigation: () => void;
    cancelNavigation: () => void;

    // Data needed for content rendering
    returnSlipId: string | number | null | undefined;
    isProcessing: boolean;
    isCompletedOrHasError: boolean;
    deleteCreditNotes: string[];
    dialogOpenedByScanner: boolean;
};

export const OrderDialogs = ({
    // Complete Return
    showCompleteDialog,
    completeReturnCheckbox,
    setCompleteReturnCheckbox,
    handleConfirmCompleteReturn,
    handleCancelCompleteReturn,

    // Complete Batch Return
    showCompleteBatchDialog,
    getUnassignedBatchItemCount,
    handleConfirmCompleteBatch,
    handleCancelCompleteBatch,

    // Delete Return
    showDeleteDialog,
    deleteReturnCheckbox,
    setDeleteReturnCheckbox,
    handleConfirmDeleteReturn,
    handleCancelDeleteReturn,

    // Processing Status
    pendingProcessingStatus,
    setPendingProcessingStatus,
    handleSaveWithStatus,
    clearHighlight,

    // Bulk Reason
    isDialogOpen,
    setIsDialogOpen,
    bulkActionCheckbox,
    setBulkActionCheckbox,
    handleConfirmBulkReason,
    cancelBulkReasonChange,

    // Bulk Max Qty
    showBulkMaxQtyDialog,
    bulkMaxQtyCheckbox,
    setBulkMaxQtyCheckbox,
    handleConfirmBulkMaxQty,
    handleCancelBulkMaxQty,

    // Remove Order
    pendingRemoveOrderNumber,
    handleConfirmRemoveOrder,
    handleCancelRemoveOrder,

    // Navigation Guard
    isNavBlocked,
    hasUnsavedChanges,
    handleConfirmNavigation,
    cancelNavigation,

    // Data
    returnSlipId,
    isProcessing,
    isCompletedOrHasError,
    deleteCreditNotes,
    dialogOpenedByScanner,
}: OrderDialogsProps) => {
    const { t } = useTranslation();
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);

    // Completing this return also credits the group companies that supplied its MDC lines. That is
    // real money moving between legal entities, so name them in the confirmation rather than
    // letting it happen quietly behind "complete return".
    const intercompanyCompanies = [
        ...new Set(
            (returnSlip?.returnItems ?? [])
                .flatMap((group) => group.orderLines ?? [])
                .filter((line) => line.isIntercompanyEligible && line.supplyingCompany)
                .map((line) => line.supplyingCompany as string),
        ),
    ].sort();

    // Not every pair is credited on the spot: one configured for manual review only receives a
    // return for someone over there to complete. Saying "this also credits them" would be a
    // straight untruth for those, and the operator would walk away believing both credits are done.
    const intercompanyPreview = useAppSelector(intercompanySelectors.selectPreview);
    const reviewOnlyCompanies = new Set(
        intercompanyPreview
            .filter((group) => group.autoCredit === false && group.supplyingCompany)
            .map((group) => group.supplyingCompany as string),
    );

    // Companies the preview says nothing about are counted as credited. That is the default and the
    // common case, and it is what this dialog said before the distinction existed.
    const creditedCompanies = intercompanyCompanies.filter((company) => !reviewOnlyCompanies.has(company));
    const reviewedCompanies = intercompanyCompanies.filter((company) => reviewOnlyCompanies.has(company));

    const intercompanySentence = [
        creditedCompanies.length > 0 &&
            t('intercompany.alsoCredits', {
                companies: creditedCompanies.join(', '),
                defaultValue: 'This also credits {{companies}} for the lines they supplied.',
            }),
        reviewedCompanies.length > 0 &&
            t('intercompany.alsoRaisesForReview', {
                companies: reviewedCompanies.join(', '),
                defaultValue:
                    '{{companies}} receive a return for the lines they supplied, which they complete themselves.',
            }),
    ]
        .filter(Boolean)
        .join(' ');

    const completeMessage = isProcessing
        ? t(
              'processingErpCreditWarning',
              'This return is being processed. Check the ERP to verify whether a credit has already been created before continuing.',
          )
        : intercompanySentence
          ? `${t('confirmCompleteOrderMessage')} ${intercompanySentence}`
          : t('confirmCompleteOrderMessage');

    return (
        <>
            {/* Complete Return Confirmation Dialog */}
            <ConfirmationDialog
                open={showCompleteDialog}
                onClose={handleCancelCompleteReturn}
                title={t('confirmCompletion')}
                content={completeMessage}
                checked={isProcessing ? undefined : completeReturnCheckbox}
                onCheckedChange={isProcessing ? undefined : setCompleteReturnCheckbox}
                onCancel={handleCancelCompleteReturn}
                onConfirm={handleConfirmCompleteReturn}
            />

            {/* Complete Batch Return Confirmation Dialog */}
            <ConfirmationDialog
                open={showCompleteBatchDialog}
                onClose={handleCancelCompleteBatch}
                title={t('completeBatchReturn', 'Complete batch return')}
                content={t('confirmCompleteBatchMessage', {
                    count: getUnassignedBatchItemCount(),
                    defaultValue:
                        '{{count}} items are not assigned to a batch return. They will not be included in any batch. Continue?',
                })}
                onCancel={handleCancelCompleteBatch}
                onConfirm={handleConfirmCompleteBatch}
            />

            {/* Delete Return Confirmation Dialog */}
            <ConfirmationDialog
                open={showDeleteDialog}
                onClose={handleCancelDeleteReturn}
                title={t('confirmDeletion', 'Confirm Deletion')}
                content={
                    isProcessing
                        ? t(
                              'processingErpCreditWarning',
                              'This return is being processed. Check the ERP to verify whether a credit has already been created before continuing.',
                          )
                        : isCompletedOrHasError && deleteCreditNotes.length
                        ? t('confirmDeleteReturnWithCredit', {
                              returnId: returnSlipId ?? 'Unknown',
                              notes: deleteCreditNotes.join(', '),
                          })
                        : t(
                              'confirmDeleteReturnMessage',
                              'Are you sure you want to delete this return {{returnId}} ? This action cannot be undone.',
                              { returnId: returnSlipId ?? 'Unknown' },
                          )
                }
                onCancel={handleCancelDeleteReturn}
                onConfirm={handleConfirmDeleteReturn}
                scannerCode={dialogOpenedByScanner ? JSON.stringify({ action: 'deleteReturn' }) : undefined}
                scannerCodeLabel={dialogOpenedByScanner ? t('configActions.deleteReturn') : undefined}
            />

            {/* Processing Status Change ERP-credit Warning Dialog */}
            <ConfirmationDialog
                open={pendingProcessingStatus !== null}
                onClose={() => setPendingProcessingStatus(null)}
                title={t('confirmProcessingStatusChange', 'Change status of a processing return?')}
                content={t(
                    'processingErpCreditWarning',
                    'This return is being processed. Check the ERP to verify whether a credit has already been created before continuing.',
                )}
                onCancel={() => setPendingProcessingStatus(null)}
                onConfirm={() => {
                    if (pendingProcessingStatus !== null) {
                        handleSaveWithStatus(pendingProcessingStatus);
                        clearHighlight();
                    }
                    setPendingProcessingStatus(null);
                }}
            />

            {/* Bulk Reason Change Confirmation Dialog */}
            <ConfirmationDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={t('confirmChangeOfReasonCodes')}
                content={t('confirmChangeOfReasonCodesMessage')}
                checked={bulkActionCheckbox}
                onCheckedChange={setBulkActionCheckbox}
                onCancel={cancelBulkReasonChange}
                onConfirm={handleConfirmBulkReason}
            />

            {/* Bulk Max Qty Confirmation Dialog */}
            <ConfirmationDialog
                open={showBulkMaxQtyDialog}
                onClose={handleCancelBulkMaxQty}
                title={t('confirmBulkMaxQty')}
                content={t('confirmBulkMaxQtyMessage')}
                checked={bulkMaxQtyCheckbox}
                onCheckedChange={setBulkMaxQtyCheckbox}
                onCancel={handleCancelBulkMaxQty}
                onConfirm={handleConfirmBulkMaxQty}
            />

            {/* Remove Order Confirmation Dialog */}
            <ConfirmationDialog
                open={!!pendingRemoveOrderNumber}
                onClose={handleCancelRemoveOrder}
                title={t('confirmRemoveOrder')}
                content={t('confirmRemoveOrderMessage', { orderNumber: pendingRemoveOrderNumber })}
                onCancel={handleCancelRemoveOrder}
                onConfirm={handleConfirmRemoveOrder}
            />

            {/* Unsaved Changes Navigation Guard Dialog */}
            <ConfirmationDialog
                open={isNavBlocked && hasUnsavedChanges}
                onClose={cancelNavigation}
                title={t('unsavedChangesTitle', 'Unsaved changes')}
                content={t(
                    'unsavedChangesMessage',
                    'You have unsaved changes. Are you sure you want to leave without saving?',
                )}
                onCancel={cancelNavigation}
                onConfirm={handleConfirmNavigation}
                cancelLabel={t('stay', 'Stay')}
                confirmLabel={t('leaveWithoutSaving', 'Leave without saving')}
                scannerCode={dialogOpenedByScanner ? JSON.stringify({ action: 'confirmNavigation' }) : undefined}
                scannerCodeLabel={
                    dialogOpenedByScanner ? t('configActions.confirmNavigation', 'Confirm leave') : undefined
                }
            />
        </>
    );
};

export default OrderDialogs;
